/**
 * 星曜解析与四化/自化注解（纯函数，可单测）
 * 将 Palace.stars 的三类字符串拆成星曜 token，并聚合每颗星的本命四化/层四化/自化标记
 */
import type { Palace, SiHuaKey, Zhi } from '../types/base';
import { parseSihua, sihuaTagsForPalace, type LayerKey, type LayerPacks, type SihuaTag } from './layer-meta';
import { GRID_POS, oppositeZhi } from './grid-layout';

/** 星曜 token（名称+亮度+类别） */
export interface StarToken {
  name: string;
  /** 亮度（庙/旺/陷/平/地/闲），无亮度为空串 */
  brightness: string;
  category: 'zhuxing' | 'fuxing' | 'zayao' | 'changsheng';
}

/** 自化箭头（归属发起宫）：离心=本宫星自化向外，向心=对宫星四化向内指对宫 */
export interface SelfArrow {
  kind: SiHuaKey;
  dir: 'out' | 'in';
  star: string;
}

/** 单颗星的四化注解 */
export interface StarAnnot {
  /** 本命四化（生年干） */
  bornKind?: SiHuaKey;
  /** 叠加层四化落点（大限/流年/流月/流日） */
  layerTags: SihuaTag[];
}

/** 自化箭头颜色（图一图例：禄绿/权紫/科蓝/忌红） */
export const SELF_ARROW_CLASS: Record<SiHuaKey, string> = {
  化禄: 'text-green-600',
  化权: 'text-purple-600',
  化科: 'text-blue-600',
  化忌: 'text-red-600',
};

/** 四化徽标单字 */
export const KIND_CHAR: Record<SiHuaKey, string> = { 化禄: '禄', 化权: '权', 化科: '科', 化忌: '忌' };

/** 自化边界箭头：离心向外字符（背离盘心/朝向对宫，对齐文墨天机） */
export const SELF_OUT_ARROW: Record<Zhi, string> = {
  巳: '↖', 午: '↑', 未: '↑', 申: '↗',
  辰: '←', 酉: '→', 卯: '←', 戌: '→',
  寅: '↙', 丑: '↓', 子: '↓', 亥: '↘',
};

const ARROW_FLIP: Record<string, string> = { '↑': '↓', '↓': '↑', '←': '→', '→': '←', '↖': '↘', '↘': '↖', '↗': '↙', '↙': '↗' };

/** 自化箭头字符：out=向外，in=向内（取反） */
export function selfArrowChar(zhi: Zhi, dir: 'out' | 'in'): string {
  const out = SELF_OUT_ARROW[zhi];
  return dir === 'out' ? out : ARROW_FLIP[out];
}

/** 边界箭头锚点（离心：跨宫格外围线半内半外，对齐文墨天机箭头压线） */
export const SELF_ARROW_POS: Record<Zhi, string> = {
  巳: 'left-0 top-0 -translate-x-1/2 -translate-y-1/2',
  午: 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2',
  未: 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2',
  申: 'right-0 top-0 translate-x-1/2 -translate-y-1/2',
  辰: 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2',
  酉: 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2',
  卯: 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2',
  戌: 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2',
  寅: 'left-0 bottom-0 -translate-x-1/2 translate-y-1/2',
  丑: 'left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2',
  子: 'left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2',
  亥: 'right-0 bottom-0 translate-x-1/2 translate-y-1/2',
};

/** 向心箭头字符：指向对宫（网格坐标差符号组合，对齐图二卯→酉↗/子→午↖/亥→巳↖） */
export function selfInArrowChar(zhi: Zhi): string {
  const p = GRID_POS[zhi];
  const o = GRID_POS[oppositeZhi(zhi)];
  const dr = Math.sign(o.row - p.row);
  const dc = Math.sign(o.col - p.col);
  const CHAR: Record<string, string> = {
    '-1,0': '↑', '1,0': '↓', '0,1': '→', '0,-1': '←',
    '-1,1': '↗', '-1,-1': '↖', '1,1': '↘', '1,-1': '↙',
  };
  return CHAR[`${dr},${dc}`] ?? '→';
}

/** 向心箭头锚点（本宫内侧线：角宫内角落点，边宫内边中点） */
export const SELF_IN_ARROW_POS: Record<Zhi, string> = {
  巳: 'right-0 bottom-0 translate-x-1/2 translate-y-1/2',
  午: 'left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2',
  未: 'left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2',
  申: 'left-0 bottom-0 -translate-x-1/2 translate-y-1/2',
  辰: 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2',
  酉: 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2',
  卯: 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2',
  戌: 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2',
  寅: 'right-0 top-0 translate-x-1/2 -translate-y-1/2',
  丑: 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2',
  子: 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2',
  亥: 'left-0 top-0 -translate-x-1/2 -translate-y-1/2',
};

/** 聚合本宫自化箭头（离心/向心均归属发起宫） */
export function palaceSelfArrows(palace: Palace): SelfArrow[] {
  const arrows: SelfArrow[] = [];
  for (const [kind, raw] of Object.entries(palace.fly) as [SiHuaKey, string][]) {
    const parsed = parseSihua(raw);
    if (parsed.self === 'centrifugal') arrows.push({ kind, dir: 'out', star: parsed.star });
    else if (parsed.self === 'centripetal') arrows.push({ kind, dir: 'in', star: parsed.star });
  }
  return arrows;
}

/** 拆 "武曲 旺，天府 庙" → token 列表 */
function splitCategory(raw: string | undefined, category: StarToken['category']): StarToken[] {
  if (!raw) return [];
  return raw.split('，').map((tok) => {
    const parts = tok.trim().split(/\s+/);
    return { name: parts[0], brightness: parts.slice(1).join(' '), category };
  });
}

/** 宫格全部星曜 token（主星→辅星→杂曜→长生，保持显示序） */
export function parseStarTokens(palace: Palace): StarToken[] {
  return [
    ...splitCategory(palace.stars?.zhuxing, 'zhuxing'),
    ...splitCategory(palace.stars?.fuxing, 'fuxing'),
    ...splitCategory(palace.stars?.zayao, 'zayao'),
    ...splitCategory(palace.stars?.changsheng, 'changsheng'),
  ];
}

/** 聚合每颗星的本命四化/层四化注解 */
export function starAnnotations(
  palace: Palace,
  layer: LayerKey,
  packs: LayerPacks,
): Map<string, StarAnnot> {
  const map = new Map<string, StarAnnot>();
  const get = (star: string): StarAnnot => {
    let a = map.get(star);
    if (!a) {
      a = { layerTags: [] };
      map.set(star, a);
    }
    return a;
  };

  // 本命四化：born = "文昌 化科，…" 每段 "星 化X"
  if (palace.born) {
    for (const seg of palace.born.split('，')) {
      const parts = seg.trim().split(/\s+/);
      if (parts.length >= 2) get(parts[0]).bornKind = parts[1] as SiHuaKey;
    }
  }

  // 层四化落点
  for (const tag of sihuaTagsForPalace(layer, packs, palace.zhi)) {
    get(tag.star).layerTags.push(tag);
  }

  return map;
}
